import { CurrentUserContext } from '@/contexts/contexts';
import { checkEmployerBenefit, useEmployerSearch } from '@/hooks/useEmployerSearch';
import { usePatientTrack } from '@/hooks/usePatientTrack';
import { BenefitAdministrator, Track } from '@bariendo/lib/constants';
import { EMPLOYER_EXTENSION } from '@bariendo/lib/extensions';
import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import { NO_EMPLOYER_BENEFIT } from '@bariendo/lib/pn-consultation-script';
import { Anchor, Autocomplete, Box, Group, Loader, Text } from '@mantine/core';
import { getExtension } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { QuotedScript, Row } from './PatientAccessCallWidgets';
import { PacLayoutContext, useAnswerWidth } from './pacLayout';

/** The partner tracks, whose administrator is already known — the Employer track in the spec's terms. */
const TRACK_ADMINISTRATOR: Partial<Record<Track, BenefitAdministrator>> = {
  [Track.CARRUM]: BenefitAdministrator.CARRUM,
  [Track.TRANSCARENT]: BenefitAdministrator.TRANSCARENT,
};

interface EmployerSearchWidgetProps<T extends PatientAccessCallAnswers> {
  widget: Extract<PacWidget, { kind: 'employerSearch' }>;
  answers: T;
  updateAnswers: (mutate: (current: T) => T) => void;
  patient?: Patient;
  readOnly?: boolean;
  /**
   * Whether the employer on file may fill the box off the Employer track. The
   * card decides: on the PN card it is off for a family member's benefit,
   * whose employer is not the one on file.
   */
  prefillFromFile?: boolean;
  /**
   * False puts nothing in the box on its own, from the file or the track: a
   * charted v2.0 note reopened takes only what the navigator enters (BAR-2996).
   */
  allowPrefill?: boolean;
}

/**
 * The employer box (BAR-2918): a typeahead over bariendo-be's employer
 * directory, and a benefit check on the employer chosen. Both go through the
 * `coverage-lookup` bot and send the employer's name only, never anything
 * about the patient.
 *
 * Stores the name under `widget.id` and the verdict under `widget.resultId` —
 * the administrator, or `none`. A failed check stores no verdict and says so:
 * an outage must never read as "no benefit".
 *
 * The bot refuses callers without a payments role, so for them the box is
 * disabled rather than left to fail. On a partner track (the Employer track)
 * the employer on file is shown, not searched, and its verdict is the track's.
 */
export function EmployerSearchWidget<T extends PatientAccessCallAnswers>({
  widget,
  answers,
  updateAnswers,
  patient,
  readOnly,
  prefillFromFile = true,
  allowPrefill = true,
}: EmployerSearchWidgetProps<T>) {
  const medplum = useMedplum();
  // `useContext`, not `useCurrentUser`: that one throws without a provider, and
  // no provider means no role, which the box handles as "can't search".
  const canLookUp = useContext(CurrentUserContext)?.currentUserPermissions.canAccessPayments ?? false;
  const { track, loading: trackLoading } = usePatientTrack(readOnly ? undefined : patient?.id);
  const columns = Boolean(useContext(PacLayoutContext));
  const answerWidth = useAnswerWidth();

  const stored = typeof answers.values[widget.id] === 'string' ? (answers.values[widget.id] as string) : '';
  const employerOnFile = patient ? getExtension(patient, EMPLOYER_EXTENSION)?.valueString?.trim() : undefined;
  const trackAdministrator = track ? TRACK_ADMINISTRATOR[track] : undefined;
  // Not on a charted note with nothing recorded: no prefill will fill it, so
  // the navigator must be able to search.
  const locked = Boolean(trackAdministrator && employerOnFile) && (allowPrefill || Boolean(stored));

  const [query, setQuery] = useState(stored);
  const { options, searching, searchFailed } = useEmployerSearch(query, {
    skip: Boolean(readOnly || locked || !canLookUp),
    current: stored,
  });
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  // An answer that changes underneath (another tab's autosave, the prefill)
  // replaces the text in the box.
  useEffect(() => setQuery(stored), [stored]);

  /** Bumped by every commit; a check whose number is no longer current changes nothing. */
  const latestCommit = useRef(0);

  /**
   * Set when an option is picked, so the Enter that picked it doesn't also
   * record the half-typed text. Mantine handles Enter for the dropdown first.
   */
  const optionPicked = useRef(false);

  /**
   * Records the employer and checks it. Whatever was recorded for the previous
   * employer goes first, so a stale verdict never sits beside a new name, and a
   * check that returns after another commit — result, failure or spinner — is
   * dropped.
   */
  const commit = useCallback(
    async (raw: string) => {
      const employer = raw.trim();
      const commitId = ++latestCommit.current;
      setCheckFailed(false);
      setChecking(Boolean(employer));
      // A cleared box stores '' rather than nothing, so the employer on file is
      // not filled back in the next time the section opens.
      updateAnswers((current) => ({
        ...current,
        values: { ...current.values, [widget.id]: employer, [widget.resultId]: undefined },
      }));
      if (!employer) return;

      try {
        const administrator = await checkEmployerBenefit(medplum, employer);
        if (commitId !== latestCommit.current) return;
        updateAnswers((current) => ({
          ...current,
          values: { ...current.values, [widget.resultId]: administrator ?? NO_EMPLOYER_BENEFIT },
        }));
      } catch {
        if (commitId === latestCommit.current) setCheckFailed(true);
      } finally {
        if (commitId === latestCommit.current) setChecking(false);
      }
    },
    [medplum, updateAnswers, widget.id, widget.resultId]
  );

  // The Employer track: the employer is known and the track says who
  // administers it, so the box shows both rather than asking. Anyone else with
  // an employer on file (typed at signup) gets it searched once, editable,
  // when the card allows it. Marked done only once it has acted, so a card
  // that allows it later (employment corrected) still gets it.
  const prefilled = useRef(false);
  useEffect(() => {
    // Only into a box never touched: a cleared one holds ''.
    const untouched = answers.values[widget.id] === undefined;
    if (readOnly || !allowPrefill || prefilled.current || trackLoading || !untouched || !employerOnFile) return;
    if (trackAdministrator) {
      prefilled.current = true;
      updateAnswers((current) => ({
        ...current,
        values: { ...current.values, [widget.id]: employerOnFile, [widget.resultId]: trackAdministrator },
      }));
    } else if (canLookUp && prefillFromFile) {
      prefilled.current = true;
      void commit(employerOnFile);
    }
  }, [
    readOnly,
    allowPrefill,
    trackLoading,
    answers.values,
    employerOnFile,
    trackAdministrator,
    canLookUp,
    prefillFromFile,
    commit,
    updateAnswers,
    widget,
  ]);

  if (readOnly || locked) {
    return (
      <Row label={widget.label} say={widget.say}>
        <Text size="sm" fw={stored ? 500 : 400} c={stored ? undefined : 'dimmed'} ta="right">
          {/* A saved note shows what it recorded, never today's employer on file. */}
          {stored || (readOnly || !allowPrefill ? undefined : employerOnFile) || '—'}
        </Text>
      </Row>
    );
  }

  const busy = searching || checking;

  return (
    <Box>
      {/* The script sits in its own column on v2.0, under the row's notices on the shipped card. */}
      <Row label={widget.label} say={columns ? widget.say : undefined}>
        <Autocomplete
          size="xs"
          w={answerWidth}
          aria-label={widget.label}
          disabled={!canLookUp}
          placeholder={canLookUp ? 'Search employers' : 'Payments role needed to search'}
          data={options}
          value={query}
          onChange={setQuery}
          // Picking an option or pressing Enter records the employer; leaving the
          // box does not, so a half-typed name is never checked or saved. Free
          // text is allowed, as at signup: Enter on a name the directory lacks.
          onOptionSubmit={(value) => {
            optionPicked.current = true;
            void commit(value);
          }}
          onKeyDown={(event) => {
            // Mantine runs this before its own Enter handling, so a pick made
            // by this key sets the flag afresh; one made by mouse is forgotten.
            optionPicked.current = false;
            if (event.key !== 'Enter') return;
            const typed = event.currentTarget.value;
            queueMicrotask(() => {
              const picked = optionPicked.current;
              optionPicked.current = false;
              if (!picked && typed.trim() !== stored) void commit(typed);
            });
          }}
          rightSection={busy ? <Loader size={12} /> : undefined}
          filter={({ options: all }) => all}
        />
      </Row>
      {checkFailed && (
        <Group gap={6} justify="flex-end" py={2}>
          <Text size="xs" c="red.7">
            Couldn&rsquo;t check benefits
          </Text>
          <Anchor component="button" type="button" size="xs" c="green.9" onClick={() => void commit(stored)}>
            Retry
          </Anchor>
        </Group>
      )}
      {searchFailed && !checkFailed && (
        <Text size="xs" c="red.7" ta="right" py={2}>
          Couldn&rsquo;t search employers
        </Text>
      )}
      {!columns && widget.say && <QuotedScript text={widget.say} />}
    </Box>
  );
}
